<template>
  <div>
    <!-- Dialog to alert user they are over the limit -->
    <v-dialog
      v-model="stopper.show"
      persistent
      overlay-opacity="0.95"
      max-width="500"
    >
      <v-card dark color="px-4 pb-4 red lighten-1">
        <v-card-title class="headline">
          <v-icon large color="white" class="ml-2 mr-2"
            >mdi-close-circle</v-icon
          >
          {{ stopper.type === 'dsr' ? 'Debt Service Ratio' : 'Networth' }} Limit
          Violation
        </v-card-title>

        <v-card-text class="mt-0 pt-2 pb-0 pl-4">
          <p style="font-size: 16px">
            You cannot proceed with this loan. The following
            {{ stopper.type == 'dsr' ? 'DSR limit(s)' : 'Networth' }} has been
            exceeded.
          </p>
          <div v-if="stopper.type == 'dsr'">
            <ol
              v-for="item in loanDistributions?.filter(
                (el) => el.affordabilityResults
              ) || []"
              :key="item.id"
            >
              <li>
                {{ `${item.person?.firstName} ${item.person?.lastName}` }}
              </li>
              <ul>
                <template
                  v-for="(ratio, index) in item.affordabilityResults
                    .dsrCalculations"
                >
                  <li :key="index" v-if="ratio.stopFlow">
                    {{ ratio.ratioName }} - {{ ratio.ratio }}% is exceeding
                    limit {{ ratio.stopperLimit }}%
                  </li>
                </template>
              </ul>
            </ol>
          </div>
        </v-card-text>
        <v-card-actions class="d-flex justify-end mt-0 pt-5">
          <v-btn
            outlined
            text
            tile
            depressed
            @click="
              this.stopper.show = false;
              $refs.terminateLoanModal.openModal();
            "
          >
            Terminate Loan
          </v-btn>

          <v-btn
            depressed
            color="white"
            class="shadow"
            tile
            light
            @click="stopper.show = false"
          >
            Close to Edit Asessment
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="d-flex justify-end"></div>
    <!-- Tabs -->
    <v-tabs v-model="tab" active-class="primary white--text">
      <v-tab class="text-capitalize text-body-2 rounded-t">
        <v-icon class="mr-3" size="20">mdi-trending-up</v-icon>
        <span> Debt Service Ratio Calculation </span>
      </v-tab>
      <v-tab class="text-capitalize text-body-2 rounded-t">
        <v-icon class="mr-3" size="20">mdi-currency-usd</v-icon>
        <span> Net-worth Calculation </span></v-tab
      >
    </v-tabs>

    <!-- Tabs for user account and summary -->
    <v-tabs
      style="border-bottom: 1px solid #ddd"
      class="mt-5"
      v-model="userSummaryTab"
    >
      <v-tab
        v-for="(item, index) in loanDistributions"
        :key="index"
        :tab-value="index"
        @click="onDSRTabChanged(index, item)"
        class="text-capitalize text-body-2 rounded-t"
      >
        <v-icon class="mr-3" size="20">mdi-account-box-outline</v-icon>

        <div class="d-flex flex-column">
          <span>{{
            `${item.person?.firstName} ${item.person?.lastName}`
          }}</span>
          <small>{{ `${item.personType}` }}</small>
        </div>
      </v-tab>
      <v-tab
        :tab-value="loanDistributions.length"
        @click="() => (userSummaryTabItems = loanDistributions.length)"
        class="text-capitalize text-body-2 rounded-t"
      >
        <v-icon class="mr-3" size="20">mdi-format-list-checkbox</v-icon>
        <span> Summary </span></v-tab
      >
    </v-tabs>
    <!-- Tab items for DSR Calculation & Networht tabs -->
    <v-tabs-items class="mt-5" v-model="tab">
      <v-tab-item class="mx-3 my-2">
        <!-- Tab items for DSR Account & Summary -->
        <v-tabs-items v-model="userSummaryTabItems">
          <v-tab-item :value="0">
            <v-row>
              <v-col cols="12" md="8">
                <!-- Tabs for account under DSR -->
                <v-tabs v-model="dsrAccountTab">
                  <v-tab class="text-capitalize text-body-2">Income</v-tab>
                  <v-tab class="text-capitalize text-body-2">Deductions</v-tab>
                  <v-tab class="text-capitalize text-body-2">Debts</v-tab>
                  <v-tab class="text-capitalize text-body-2">Expenses</v-tab>
                </v-tabs>
                <v-tabs-items v-model="dsrAccountTab">
                  <v-tab-item class="pa-2">
                    <DSRIncomeTable
                      :incomeTypes="incomeTypes"
                      :incomes="
                        selectedLoanDistribution?.affordabilityResults?.incomes
                      "
                      @update="onItemsUpdated('incomes', $event)"
                    />
                  </v-tab-item>
                  <v-tab-item class="pa-2">
                    <DSRDeductionTable
                      :totalIncome="
                        +selectedLoanDistribution?.affordabilityResults
                          ?.totalIncome
                      "
                      :totalTaxableIncome="
                        +selectedLoanDistribution?.affordabilityResults
                          ?.totalTaxableIncome
                      "
                      :nonStatutoryDeductionTypes="nonStatutoryDeductionTypes"
                      :statutoryDeductionTypes="statutoryDeductionTypes"
                      :deductions="
                        selectedLoanDistribution?.affordabilityResults
                          ?.deductions
                      "
                      @update="onItemsUpdated('deductions', $event)"
                    />
                  </v-tab-item>
                  <v-tab-item class="pa-2">
                    <DSRDebtTable
                      :debts="
                        selectedLoanDistribution?.affordabilityResults
                          ?.liabilities
                      "
                      :debtTypes="liabilityTypes"
                      @update="onItemsUpdated('liabilities', $event)"
                    />
                  </v-tab-item>
                  <v-tab-item class="pa-2">
                    <DSRExpensesTable
                      :expenses="
                        selectedLoanDistribution?.affordabilityResults?.expenses
                      "
                      :expenseTypes="expenseTypes"
                      @update="onItemsUpdated('expenses', $event)"
                    />
                  </v-tab-item>
                </v-tabs-items>
              </v-col>
              <v-col cols="12" md="4">
                <!-- Dsr summary card -->
                <DSRSummaryCard
                  :affordabilityResults="
                    selectedLoanDistribution?.affordabilityResults
                  "
                />
              </v-col>
            </v-row>
          </v-tab-item>
          <v-tab-item :value="loanDistributions.length">
            <!-- Sumarry table for DSR -->
            <DSRSummaryTable :loanDistributions="loanDistributions" />
          </v-tab-item>
        </v-tabs-items>
      </v-tab-item>
      <v-tab-item>
        <!-- Tab items for Networht Account & Summary -->
        <v-tabs-items v-model="userSummaryTabItems">
          <v-tab-item :value="0">
            <v-row>
              <v-col cols="12" md="8">
                <!-- Tabs for account under DSR -->
                <v-tabs v-model="netAccountTab">
                  <v-tab class="text-capitalize text-body-2">Assets</v-tab>
                  <v-tab class="text-capitalize text-body-2">Liabilities</v-tab>
                </v-tabs>

                <v-tabs-items v-model="netAccountTab">
                  <v-tab-item class="pa-2">
                    <NETAssetsTable
                      :assets="
                        selectedLoanDistribution?.affordabilityResults?.assets
                      "
                      :assetTypes="assetTypes"
                      @update="onItemsUpdated('assets', $event)"
                    />
                  </v-tab-item>
                  <v-tab-item class="pa-2">
                    <NETLiabilityTable
                      :liabilities="
                        selectedLoanDistribution?.affordabilityResults
                          ?.liabilities
                      "
                      :liabilityTypes="liabilityTypes"
                      @update="onItemsUpdated('liabilities', $event)"
                    />
                  </v-tab-item>
                </v-tabs-items>
              </v-col>
              <v-col cols="12" md="4">
                <!-- NET summary card -->
                <NETSummaryCard
                  :affordabilityResults="
                    selectedLoanDistribution?.affordabilityResults
                  "
                />
              </v-col>
            </v-row>
          </v-tab-item>
          <v-tab-item :value="loanDistributions.length">
            <!-- Summary table for Net worth -->
            <NETSummaryTable :loanDistributions="loanDistributions" />
          </v-tab-item>
        </v-tabs-items>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
// DSR summary card
import DSRSummaryCard from '@/modules/loanpro/components/loan-pro/WorkLoad/Affordability/DSRCalculation/SummaryCard.vue';

// DSR Tables
import DSRIncomeTable from '@/modules/loanpro/components/loan-pro/WorkLoad/Affordability/DSRCalculation/IncomeTable.vue';
import DSRDeductionTable from '@/modules/loanpro/components/loan-pro/WorkLoad/Affordability/DSRCalculation/DeductionTable.vue';
import DSRDebtTable from '@/modules/loanpro/components/loan-pro/WorkLoad/Affordability/DSRCalculation/DebtTable.vue';
import DSRExpensesTable from '@/modules/loanpro/components/loan-pro/WorkLoad/Affordability/DSRCalculation/ExpensesTable.vue';
import DSRSummaryTable from '@/modules/loanpro/components/loan-pro/WorkLoad/Affordability/DSRCalculation/SummaryTable.vue';

// Net summary card
import NETSummaryCard from '@/modules/loanpro/components/loan-pro/WorkLoad/Affordability/NetCalculation/SummaryCard.vue';
// Net worth imports
import NETSummaryTable from '@/modules/loanpro/components/loan-pro/WorkLoad/Affordability/NetCalculation/SummaryTable.vue';
import NETAssetsTable from '@/modules/loanpro/components/loan-pro/WorkLoad/Affordability/NetCalculation/AssetsTable.vue';
import NETLiabilityTable from '@/modules/loanpro/components/loan-pro/WorkLoad/Affordability/NetCalculation/LiabilityTable.vue';

import RatioCalculatorService from '@/modules/loanpro/services/ratio-calculation.service';
import { cloneDeep } from 'lodash';

export default {
  components: {
    DSRSummaryCard,
    NETSummaryCard,
    DSRIncomeTable,
    DSRDeductionTable,
    DSRDebtTable,
    DSRExpensesTable,
    DSRSummaryTable,
    // Net COmps
    NETSummaryTable,
    NETAssetsTable,
    NETLiabilityTable
  },

  props: {
    distributions: {
      type: Array,
      default: () => []
    }
  },

  watch: {
    distributions: {
      deep: true,
      immediate: true,
      handler: function (val) {
        this.loanDistributions = val;

        if (
          !this.selectedLoanDistribution?.personId &&
          this.loanDistributions.length > 0
        ) {
          const index = this.loanDistributions.findIndex(
            (el) =>
              el.personType !== 'co-maker' && el.distributionType === 'internal'
          );
          this.selectedLoanDistribution = this.loanDistributions[index];
        }

        const index = this.loanDistributions.findIndex(
          (el) =>
            el.personId === this.selectedLoanDistribution?.personId &&
            el.distributionType === 'internal'
        );
        this.selectedLoanDistribution = this.loanDistributions[index];
        if (this.selectedLoanDistribution) {
          this.calculateRatios();
          this.calculateNetRatio();
        }
      }
    }
  },

  data() {
    return {
      tab: 0,
      stopper: {
        show: false,
        type: 'dsr'
      },
      networthLimit: -1,
      loanDistributions: [],
      userSummaryTab: 0,
      userSummaryTabItems: 0,
      dsrAccountTab: 0,
      netAccountTab: 0,
      selectedLoanDistribution: {},
      selectedLoanDistributionIndex: 0
    };
  },

  async created() {
    this.selectedLoanDistribution = this.loanDistributions[0];
  },

  computed: {
    ...mapGetters('loanSettingsModule', [
      'dsrCalculationMethods',
      'incomeTypes',
      'expenseTypes',
      'statutoryDeductionTypes',
      'nonStatutoryDeductionTypes',
      'deductionTypes',
      'assetTypes',
      'liabilityTypes',
      'dsrRatings',
      'networthRatings',
      'loanStoppers'
    ])
  },

  methods: {
    async validate() {
      this.stopper.show = false;
      const shouldStopFlow = !!this.loanDistributions.filter((el) =>
        el.affordabilityResults?.dsrCalculations?.some((e) => e.stopFlow)
      ).length;

      if (shouldStopFlow) {
        this.stopper.type = 'dsr';
        this.stopper.show = true;
        return {
          valid: false,
          message: 'One or more DSR Calculation has exceeded the System Limit.'
        };
      }

      const personsWithoutIncome = this.loanDistributions
        .filter((el) => !el.affordabilityResults?.incomes?.length)
        .map((el) => `${el.person.firstName} ${el.person.lastName}`);
      if (personsWithoutIncome.length) {
        return {
          valid: false,
          message: `${personsWithoutIncome[0]} needs at least one income to proceed.`
        };
      }

      return { valid: true };
    },

    onDSRTabChanged(index, loanDistribution = {}) {
      this.selectedLoanDistributionIndex = -1;
      this.selectedLoanDistribution = loanDistribution;
      this.selectedLoanDistributionIndex = index;
      this.$nextTick(() => {
        this.userSummaryTab = index;
        this.userSummaryTabItems = 0;
      });
    },

    onItemsUpdated(type, data = {}) {
      this.selectedLoanDistribution.affordabilityResults[type] = data;
      if (type == 'incomes') {
        this.setTotalIncomes();
      }
      this.calculateRatios();
      this.calculateNetRatio();

      //Emit average dsr to store on the loan
      this.$emit('updated', this.loanDistributions);
    },

    setTotalIncomes() {
      const results = cloneDeep(
        this.selectedLoanDistribution.affordabilityResults
      );
      results.totalIncome = results.incomes.reduce(
        (acc, cur) => (acc += +cur.amount),
        0
      );

      results.totalTaxableIncome = results.incomes
        .filter((el) => {
          return this.incomeTypes.find(
            (elx) =>
              elx.accountName.toLowerCase() == el.accountName.toLowerCase() &&
              elx.taxable
          );
        })
        .reduce((acc, cur) => (acc += +Number(cur.amount)), 0);

      this.selectedLoanDistribution.affordabilityResults = results;
    },

    calculateRatios() {
      let averageDSR = 0;
      let mainDsrRatio = 0;

      this.loanDistributions.forEach((loanDistribution, index) => {
        const isSelectedIndex = index === this.selectedLoanDistributionIndex;
        const distribution = isSelectedIndex
          ? this.selectedLoanDistribution
          : loanDistribution;
        const { deductions, liabilities, expenses, incomes } =
          distribution.affordabilityResults;

        //total incomes, expenses, debts and deductions
        const totalStatutoryDeductions =
          deductions?.find(
            (el) =>
              el.isTaxDeductible && el.accountName == 'Total Tax Deduction'
          )?.amount || 0;

        const totalNonStatutoryDeductions =
          deductions
            ?.filter((el) => el.accountName !== 'Total Tax Deduction')
            ?.reduce((acc, cur) => (acc += +cur.amount), 0) || 0;

        const grossIncome =
          incomes?.reduce((acc, cur) => (acc += +cur.amount), 0) || 0;
        const totalDebts =
          liabilities?.reduce((acc, cur) => (acc += +cur.monthlyAmount), 0) ||
          0;
        const totalExpenses =
          expenses?.reduce((acc, cur) => (acc += +cur.amount), 0) || 0;
        const totalPayments =
          totalDebts + totalExpenses + totalNonStatutoryDeductions;
        const finalIncome = grossIncome - totalPayments;
        const totalTaxableIncome =
          incomes
            ?.filter((el) => {
              return this.incomeTypes.find(
                (elx) =>
                  elx.accountName.toLowerCase() ==
                    el.accountName.toLowerCase() && elx.taxable
              );
            })
            .reduce((acc, cur) => (acc += +Number(cur.amount)), 0) || 0;

        //calculate dsr ratios based on settongs
        const ratioResults = [];
        const calculationMethods =
          this.loanDistributions?.dsrCalculationMethods?.filter(
            (e) => e.isActive
          ) || this.dsrCalculationMethods?.filter((e) => e.isActive);

        calculationMethods?.forEach((method) => {
          const calculator = new RatioCalculatorService(
            {
              dsrRatings: this.dsrRatings,
              dsrMethodSettings: method
            },
            {
              totalIncome: grossIncome,
              totalDebts: totalDebts,
              totalExpenses: totalExpenses,
              totalStatutoryDeductions: totalStatutoryDeductions,
              totalNonStatutoryDeductions:
                totalNonStatutoryDeductions - totalStatutoryDeductions
            }
          );

          const result = calculator.getDSRResults();
          ratioResults.push(result);

          if (result.isCreditScore) {
            mainDsrRatio = +result.ratio.toFixed(2);
            averageDSR += +(
              result.ratio / this.loanDistributions.length
            ).toFixed(2);
          }
        });

        const updatedDistribution = {
          ...distribution,
          affordabilityResults: {
            ...distribution.affordabilityResults,
            mainDsrRatio,
            grossIncome,
            finalIncome,
            totalDebts,
            totalTaxableIncome,
            totalPayments,
            totalExpenses,
            totalStatutoryDeductions,
            totalNonStatutoryDeductions,
            dsrCalculations: ratioResults,
            dsrCalculationMethods: this.dsrCalculationMethods
          }
        };

        //update affordabilityResults object with totals and calculated ratio results
        this.loanDistributions.splice(index, 1, updatedDistribution);

        //update selected distribution
        if (isSelectedIndex) {
          this.selectedLoanDistribution = updatedDistribution;
        }
      });
    },

    calculateNetRatio() {
      let averageNetRatio = 0;

      this.loanDistributions.forEach((loanDistribution, index) => {
        const isSelectedIndex = index === this.selectedLoanDistributionIndex;
        const distribution = isSelectedIndex
          ? this.selectedLoanDistribution
          : loanDistribution;
        const { liabilities, assets } = distribution.affordabilityResults;

        //total assets and liabilities
        const totalLiabilities =
          liabilities?.reduce(
            (acc, cur) => (acc += +cur.totalPayment || 0),
            0
          ) || 0;
        const totalAssets =
          assets?.reduce((acc, cur) => (acc += +cur.internalValue), 0) || 0;

        const calculator = new RatioCalculatorService(
          {
            networthRatings: this.networthRatings
          },
          {
            totalAssets,
            totalLiabilities
          }
        );

        const result = calculator.getNetworthResults();
        averageNetRatio += result.netRatio / this.loanDistributions.length;

        const updatedDistribution = {
          ...distribution,
          affordabilityResults: {
            ...distribution.affordabilityResults,
            ...result
          }
        };

        //update affordabilityResults object with totals and calculated ratio results
        this.loanDistributions.splice(index, 1, updatedDistribution);

        //update selected distribution
        if (isSelectedIndex) {
          this.selectedLoanDistribution = updatedDistribution;
        }
      });
    }
  }
};
</script>
